import { useState, useId, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Minus,
  Plus,
  ShoppingCart,
  Sun,

  Thermometer,
  Gauge,
  Star,
  Loader2,
  Heart,
  Share2,
  X,
  Shovel,
  Maximize2,
  Sprout,
  Calendar,
  ArrowUpDown,
  Ruler,
  Copy,
  Check,
  ThumbsUp,
  Flag,
  Search,
  ShieldCheck,
  CheckCircle,
} from 'lucide-react';
import { useProduct, useProductReviews, useProducts, useWishlist, useSettings } from '@/hooks/useProducts';
import ReviewForm from '@/components/ReviewForm';
import ReviewGallery from '@/components/ReviewGallery';
import api from '@/lib/api';
import { useCartStore } from '@/stores/cart-store';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import LowStockIndicator from '@/components/upsell/LowStockIndicator';
import FrequentlyBoughtTogether from '@/components/upsell/FrequentlyBoughtTogether';
import ProductBundles from '@/components/upsell/ProductBundles';
import VolumeDiscountTable from '@/components/upsell/VolumeDiscountTable';
import CountdownTimer from '@/components/upsell/CountdownTimer';
import BackInStockButton from '@/components/upsell/BackInStockButton';
import RecentlyViewed from '@/components/upsell/RecentlyViewed';
import DocumentHead from '@/components/DocumentHead';
import JsonLd from '@/components/JsonLd';
import QuickEditWrapper from '@/components/quickedit/QuickEditWrapper';
import { SITE_NAME, SITE_SHORT_NAME, SITE_URL } from '@/config/site';
import StickyAddToCart from '@/components/StickyAddToCart';
import BrandSymbol from '@/components/BrandSymbol';
import { trackViewedProduct } from '@/lib/integrations/storefront-events';
import YotpoReviewsWidget, { YotpoStarRating, useYotpoPublic } from '@/components/integrations/YotpoReviews';
import StampedReviewsWidget, { StampedStarRating, useStampedPublic } from '@/components/integrations/StampedReviews';

function ImageLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  const { t } = useTranslation('product');
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('imageGallery.lightboxAriaLabel')}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
        aria-label={t('imageGallery.closePreview')}
      >
        <X className="h-6 w-6" />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-[90vh] max-w-[90vw] object-contain touch-pinch-zoom"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

function ImageGallery({ images }: { images: any[] }) {
  const { t } = useTranslation('product');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const mainImage = images[selectedIndex];

  return (
    <div className="space-y-4">
      {/* Main image */}
      <div
        className="aspect-square overflow-hidden rounded-xl bg-surface-muted cursor-zoom-in"
        onClick={() => mainImage?.url && setLightboxOpen(true)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && mainImage?.url) {
            e.preventDefault();
            setLightboxOpen(true);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={t('imageGallery.zoomAriaLabel')}
      >
        {mainImage?.url ? (
          <img
            src={mainImage.url}
            alt={mainImage.alt || t('imageGallery.productImageAlt')}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <BrandSymbol className="h-16 w-16 text-ink-muted" />
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto" role="group" aria-label={t('imageGallery.groupAriaLabel')}>
          {images.map((img: any, index: number) => (
            <button
              key={img.id || index}
              onClick={() => setSelectedIndex(index)}
              className={cn(
                'h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2',
                index === selectedIndex
                  ? 'border-sage-600'
                  : 'border-line hover:border-line-strong'
              )}
              aria-label={t('imageGallery.viewImage', { index: index + 1 })}
              aria-pressed={index === selectedIndex}
            >
              <img
                src={img.thumbnail_url || img.url}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && mainImage?.url && (
        <ImageLightbox
          src={mainImage.url}
          alt={mainImage.alt || t('imageGallery.productImageAlt')}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

function VariantSelector({
  variants,
  selectedId,
  onChange,
}: {
  variants: any[];
  selectedId: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation('product');
  const groupId = useId();

  if (!variants || variants.length <= 1) return null;

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold text-ink">
        {t('variantSelectorLegend')}
      </legend>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby={groupId}>
        {variants.map((variant: any) => (
          <button
            key={variant.id}
            onClick={() => onChange(variant.id)}
            role="radio"
            aria-checked={variant.id === selectedId}
            className={cn(
              'rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2',
              variant.id === selectedId
                ? 'border-sage-600 bg-sage-50 text-sage-800'
                : 'border-line text-ink-soft hover:border-line-strong'
            )}
          >
            {variant.name}
            <span className="ml-2 text-ink-muted">
              {formatCurrency(variant.price)}
            </span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function QuantityInput({
  value,
  onChange,
  max,
}: {
  value: number;
  onChange: (n: number) => void;
  max: number;
}) {
  const { t } = useTranslation('product');
  return (
    <div className="flex items-center gap-1">
      <label className="sr-only">{t('quantity')}</label>
      <button
        onClick={() => onChange(Math.max(1, value - 1))}
        disabled={value <= 1}
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-lg border border-line-strong text-ink-soft transition-colors',
          'hover:bg-surface-soft disabled:opacity-50 disabled:pointer-events-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500'
        )}
        aria-label={t('decrementQuantity')}
      >
        <Minus className="h-4 w-4" aria-hidden="true" />
      </button>
      <input
        type="number"
        min={1}
        max={max}
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          // i18n-exempt: arithmetic comparison, not JSX text
          if (!isNaN(n) && n >= 1 && n <= max) onChange(n);
        }}
        className={cn(
          'h-10 w-14 rounded-lg border border-line-strong text-center text-sm font-medium text-ink',
          'focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-transparent'
        )}
        aria-label={t('quantity')}
      />
      <button
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-lg border border-line-strong text-ink-soft transition-colors',
          'hover:bg-surface-soft disabled:opacity-50 disabled:pointer-events-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500'
        )}
        aria-label={t('incrementQuantity')}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

function GrowingInfoBadges({ product }: { product: any }) {
  const { t } = useTranslation('product');
  const zoneValue = product.zone_min && product.zone_max
    ? `${product.zone_min}-${product.zone_max}`
    : product.zone_min || product.zone_max;

  const badges = [
    {
      icon: <Thermometer className="h-5 w-5" aria-hidden="true" />,
      label: t('growingInfo.zone'),
      value: zoneValue,
    },
    {
      icon: <Sun className="h-5 w-5" aria-hidden="true" />,
      label: t('growingInfo.sun'),
      value: product.sun,
    },
    {
      icon: <Gauge className="h-5 w-5" aria-hidden="true" />,
      label: t('growingInfo.difficulty'),
      value: product.difficulty_level,
    },
  ].filter((b) => b.value);

  if (badges.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" role="list" aria-label={t('growingInfo.ariaLabel')}>
      {badges.map((badge) => (
        <div
          key={badge.label}
          role="listitem"
          className="flex flex-col items-center gap-1.5 rounded-lg border border-line bg-surface-soft p-3 text-center"
        >
          <span className="text-sage-700">{badge.icon}</span>
          <span className="text-xs font-medium text-ink-muted">{badge.label}</span>
          <span className="text-sm font-semibold text-ink">{badge.value}</span>
        </div>
      ))}
    </div>
  );
}

function ProductTags({ tags }: { tags: any[] }) {
  if (!tags || tags.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag: any) => {
        const name = typeof tag === 'string' ? tag : tag.name;
        const slug = typeof tag === 'string' ? tag.toLowerCase().replace(/\s+/g, '-') : tag.slug || tag.name?.toLowerCase().replace(/\s+/g, '-');
        return (
          <Link
            key={slug}
            to={`/shop?tag=${slug}`}
            className="inline-flex items-center rounded-full border border-line bg-surface px-3 py-1 text-xs font-medium text-ink-soft hover:border-sage-300 hover:text-sage-700 transition-colors"
          >
            {name}
          </Link>
        );
      })}
    </div>
  );
}

function SocialShareButtons({ product, imageUrl }: { product: any; imageUrl?: string }) {
  const { t } = useTranslation('product');
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/product/${product.slug}`;
  const text = t('shareText', { name: product.name, site: SITE_NAME });

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  }

  const buttons = [
    {
      label: t('shareButtons.facebook'),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      icon: (
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
        </svg>
      ),
    },
    {
      label: t('shareButtons.twitter'),
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      icon: (
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
    },
    {
      label: t('shareButtons.pinterest'),
      href: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(imageUrl || '')}&description=${encodeURIComponent(text)}`,
      icon: (
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="flex items-center gap-2">
      <Share2 className="h-4 w-4 text-ink-muted" aria-hidden="true" />
      {buttons.map((btn) => (
        <a
          key={btn.label}
          href={btn.href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-ink-muted hover:border-line-strong hover:text-ink-soft transition-colors"
          title={btn.label}
          aria-label={btn.label}
        >
          {btn.icon}
        </a>
      ))}
      <button
        onClick={copyLink}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-ink-muted hover:border-line-strong hover:text-ink-soft transition-colors"
        title={copied ? t('shareButtons.copied') : t('shareButtons.copyLink')}
        aria-label={t('shareButtons.copyLink')}
      >
        {copied ? <Check className="h-4 w-4 text-sage-600" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function WishlistButton({ productId }: { productId: string }) {
  const { t } = useTranslation('product');
  const user = useAuthStore((s) => s.user);
  const addToast = useUIStore((s) => s.addToast);
  const { data: wishlistData, refetch } = useWishlist();
  const [isToggling, setIsToggling] = useState(false);

  const isInWishlist = (wishlistData?.items ?? []).some(
    (item: any) => item.product_id === productId
  );

  async function toggleWishlist() {
    if (!user) {
      addToast(t('wishlist.loginPrompt'), 'info');
      return;
    }
    setIsToggling(true);
    try {
      if (isInWishlist) {
        await api.delete(`/wishlist/${productId}`);
        addToast(t('wishlist.removedToast'), 'success');
      } else {
        await api.post('/wishlist', { product_id: productId });
        addToast(t('wishlist.addedToast'), 'success');
      }
      refetch();
    } catch {
      addToast(t('wishlist.failed'), 'error');
    } finally {
      setIsToggling(false);
    }
  }

  return (
    <button
      onClick={toggleWishlist}
      disabled={isToggling}
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-lg border-2 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2',
        isInWishlist
          ? 'border-red-200 bg-red-50 text-red-500 hover:bg-red-100'
          : 'border-line text-ink-muted hover:border-line-strong hover:text-ink-soft'
      )}
      aria-label={isInWishlist ? t('removeFromWishlist') : t('addToWishlist')}
    >
      <Heart className={cn('h-5 w-5', isInWishlist && 'fill-current')} />
    </button>
  );
}

const SEASON_COLORS: Record<string, string> = {
  spring: 'bg-sage-100 text-sage-800',
  summer: 'bg-yellow-100 text-yellow-800',
  fall: 'bg-orange-100 text-orange-800',
  winter: 'bg-blue-100 text-blue-800',
};

function GrowingGuideGrid({ product }: { product: any }) {
  const { t } = useTranslation('product');
  const cards: { icon: React.ReactNode; label: string; value: string }[] = [];

  if (product.planting_depth) {
    cards.push({ icon: <Shovel className="h-5 w-5" />, label: t('growingGuide.plantingDepth'), value: product.planting_depth });
  }
  if (product.plant_spacing || product.spacing) {
    cards.push({ icon: <Maximize2 className="h-5 w-5" />, label: t('growingGuide.plantSpacing'), value: product.plant_spacing || product.spacing });
  }
  if (product.days_to_germination || product.days_to_germination_min) {
    const val = product.days_to_germination || (product.days_to_germination_min && product.days_to_germination_max
      ? t('growingGuide.daysRange', { min: product.days_to_germination_min, max: product.days_to_germination_max })
      : t('growingGuide.daysSingle', { value: product.days_to_germination_min }));
    cards.push({ icon: <Sprout className="h-5 w-5" />, label: t('growingGuide.daysToGermination'), value: val });
  }
  if (product.days_to_maturity || product.days_to_maturity_min) {
    const val = product.days_to_maturity || (product.days_to_maturity_min && product.days_to_maturity_max
      ? t('growingGuide.daysRange', { min: product.days_to_maturity_min, max: product.days_to_maturity_max })
      : t('growingGuide.daysSingle', { value: product.days_to_maturity_min }));
    cards.push({ icon: <Calendar className="h-5 w-5" />, label: t('growingGuide.daysToMaturity'), value: val });
  }
  if (product.row_spacing) {
    cards.push({ icon: <ArrowUpDown className="h-5 w-5" />, label: t('growingGuide.rowSpacing'), value: product.row_spacing });
  }
  if (product.height_min || product.height_max) {
    const val = product.height_min && product.height_max
      ? `${product.height_min}–${product.height_max}″`
      : `${product.height_min || product.height_max}″`;
    cards.push({ icon: <Ruler className="h-5 w-5" />, label: t('growingGuide.height'), value: val });
  }

  const hasSeasons = (product.planting_seasons?.length > 0) || (product.harvest_seasons?.length > 0);
  const hasCards = cards.length > 0;

  if (!hasCards && !hasSeasons) {
    return <p className="text-ink-muted">{t('growingGuide.notAvailable')}</p>;
  }

  return (
    <div className="space-y-6">
      {hasCards && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {cards.map((card) => (
            <div
              key={card.label}
              className="flex items-center gap-3 rounded-lg border border-line bg-surface-soft p-4"
            >
              <span className="text-sage-700">{card.icon}</span>
              <div>
                <p className="text-xs font-medium text-ink-muted">{card.label}</p>
                <p className="text-sm font-semibold text-ink">{card.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {product.planting_seasons?.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium text-ink-soft">{t('growingGuide.plantingSeasons')}</p>
          <div className="flex flex-wrap gap-2">
            {product.planting_seasons.map((season: string) => (
              <span
                key={season}
                className={cn('rounded-full px-3 py-1 text-xs font-medium capitalize', SEASON_COLORS[season] || 'bg-surface-muted text-ink-soft')}
              >
                {season}
              </span>
            ))}
          </div>
        </div>
      )}

      {product.harvest_seasons?.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium text-ink-soft">{t('growingGuide.harvestSeasons')}</p>
          <div className="flex flex-wrap gap-2">
            {product.harvest_seasons.map((season: string) => (
              <span
                key={season}
                className={cn('rounded-full px-3 py-1 text-xs font-medium capitalize', SEASON_COLORS[season] || 'bg-surface-muted text-ink-soft')}
              >
                {season}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  // i18n-exempt: numeric "X out of 5 stars" — locale-aware formatting deferred
  // to Phase 1c.9 with the wider Intl.NumberFormat pass.
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            'h-4 w-4',
            star <= rating ? 'fill-amber-400 text-amber-400' : 'text-ink-muted'
          )}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function Tabs({ product }: { product: any }) {
  const { t } = useTranslation('product');
  const yotpo = useYotpoPublic();
  const stamped = useStampedPublic();
  const hideNativeReviews = !!yotpo?.hide_native || !!stamped?.hide_native;
  const [activeTab, setActiveTab] = useState<'description' | 'growing' | 'reviews'>(
    'description'
  );
  const { slug } = useParams<{ slug: string }>();
  const [reviewPage, setReviewPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [ratingFilter, setRatingFilter] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [reportingReview, setReportingReview] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState('spam');
  const [reportDescription, setReportDescription] = useState('');

  const queryParams = new URLSearchParams({ page: String(reviewPage) });
  if (sortBy !== 'created_at') queryParams.set('sort', sortBy);
  if (ratingFilter) queryParams.set('rating', ratingFilter);
  if (verifiedOnly) queryParams.set('verified', 'true');
  if (searchQuery) queryParams.set('q', searchQuery);

  const { data: reviewsData, isLoading: reviewsLoading } = useProductReviews(
    slug || '',
    reviewPage,
    { sort: sortBy, rating: ratingFilter, verified: verifiedOnly ? 'true' : '', q: searchQuery }
  );
  const reviews = reviewsData?.reviews ?? [];
  const ratingDistribution = (reviewsData as any)?.rating_distribution ?? [];
  const attributeAverages = (reviewsData as any)?.attribute_averages ?? [];
  const pagination = (reviewsData as any)?.pagination;
  const galleryImages = reviews.flatMap((r: any) =>
    (r.images || []).map((img: any) => ({ ...img, review_id: r.id, rating: r.rating, excerpt: r.body?.slice(0, 100), first_name: r.first_name }))
  );

  const addToast = useUIStore((s) => s.addToast);

  async function handleHelpful(reviewId: string) {
    try {
      await api.post(`/reviews/${reviewId}/helpful`, {});
    } catch {
      // silent fail for non-authenticated
    }
  }

  async function handleReport() {
    if (!reportingReview) return;
    try {
      await api.post(`/reviews/${reportingReview}/report`, {
        reason: reportReason,
        description: reportDescription || undefined,
      });
      addToast(t('reviews.reportToast'), 'success');
      setReportingReview(null);
      setReportDescription('');
    } catch (err: any) {
      addToast(err.message || t('reviews.reportFailed'), 'error');
    }
  }

  const hasGrowingData = product?.plant_lifecycle || product?.sun_requirement || product?.growing_instructions;
  const tabs = [
    { id: 'description' as const, label: t('tabs.description') },
    ...(hasGrowingData ? [{ id: 'growing' as const, label: t('tabs.growingGuide') }] : []),
    {
      id: 'reviews' as const,
      label: pagination?.total
        ? t('tabs.reviewsWithCount', { count: pagination.total })
        : t('tabs.reviews'),
    },
  ];

  return (
    <div>
      <div role="tablist" aria-label={t('tabs.ariaLabel')} className="flex border-b border-line">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-inset',
              activeTab === tab.id
                ? 'border-sage-600 text-sage-700'
                : 'border-transparent text-ink-muted hover:text-ink-soft hover:border-line-strong'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Description */}
      <div
        id="tabpanel-description"
        role="tabpanel"
        aria-labelledby="tab-description"
        hidden={activeTab !== 'description'}
        className="py-6"
      >
        <div
          className="prose prose-stone max-w-none prose-headings:font-semibold prose-a:text-sage-700 prose-img:rounded-lg"
          dangerouslySetInnerHTML={{
            __html: product.description || `<p>${t('noDescription')}</p>`,
          }}
        />
      </div>

      {/* Growing Guide */}
      <div
        id="tabpanel-growing"
        role="tabpanel"
        aria-labelledby="tab-growing"
        hidden={activeTab !== 'growing'}
        className="py-6"
      >
        {product.growing_guide ? (
          <div
            className="prose prose-stone max-w-none prose-headings:font-semibold"
            dangerouslySetInnerHTML={{ __html: product.growing_guide }}
          />
        ) : (
          <GrowingGuideGrid product={product} />
        )}
      </div>

      {/* Reviews */}
      <div
        id="tabpanel-reviews"
        role="tabpanel"
        aria-labelledby="tab-reviews"
        hidden={activeTab !== 'reviews'}
        className="py-6"
      >
        {yotpo && (
          <div className="mb-6 space-y-4" data-testid="yotpo-reviews">
            <YotpoStarRating productId={product.id} />
            <YotpoReviewsWidget product={product} />
          </div>
        )}
        {stamped && (
          <div className="mb-6 space-y-4" data-testid="stamped-reviews">
            <StampedStarRating productId={product.id} />
            <StampedReviewsWidget product={product} />
          </div>
        )}
        {!hideNativeReviews && (<>
        {/* Rating Distribution Summary */}
        {ratingDistribution.length > 0 && (
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="text-3xl font-bold text-ink">
                {product.avg_rating ? parseFloat(product.avg_rating).toFixed(1) : '—'}
              </div>
              <StarRating rating={parseFloat(product.avg_rating) || 0} />
              <p className="text-sm text-ink-muted mt-1">{t('reviews.totalReviews', { count: pagination?.total || 0 })}</p>
              <div className="mt-3 space-y-1">
                {[5, 4, 3, 2, 1].map((star) => {
                  const dist = ratingDistribution.find((d: any) => parseInt(d.rating) === star);
                  const count = dist ? parseInt(dist.count) : 0;
                  const pct = pagination?.total ? (count / pagination.total) * 100 : 0;
                  return (
                    <button
                      key={star}
                      onClick={() => setRatingFilter(ratingFilter === String(star) ? '' : String(star))}
                      className={cn(
                        'flex items-center gap-2 w-full text-xs hover:bg-surface-soft rounded px-1 py-0.5',
                        ratingFilter === String(star) && 'bg-sage-50'
                      )}
                    >
                      <span className="w-8">{t('reviews.starsLabel', { count: star })}</span>
                      <div className="flex-1 bg-surface-muted rounded-full h-2">
                        <div className="bg-amber-400 rounded-full h-2" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-right text-ink-muted">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Attribute Averages */}
            {attributeAverages.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-ink-soft mb-2">{t('reviews.aspectRatingsHeading')}</h4>
                <div className="space-y-2">
                  {attributeAverages.map((attr: any) => {
                    const label = attr.key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
                    return (
                      <div key={attr.key} className="flex items-center justify-between gap-2">
                        <span className="text-sm text-ink-soft">{label}</span>
                        <div className="flex items-center gap-1">
                          <StarRating rating={parseFloat(attr.avg_value)} />
                          <span className="text-xs text-ink-muted">({attr.count})</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setReviewPage(1); }}
            className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
            aria-label={t('reviews.sortLabel')}
          >
            <option value="created_at">{t('reviews.sortOptions.mostRecent')}</option>
            <option value="helpful">{t('reviews.sortOptions.mostHelpful')}</option>
            <option value="rating_high">{t('reviews.sortOptions.highestRated')}</option>
            <option value="rating_low">{t('reviews.sortOptions.lowestRated')}</option>
          </select>

          {/* Verified filter */}
          <label className="flex items-center gap-1.5 text-sm text-ink-soft cursor-pointer">
            <input
              type="checkbox"
              checked={verifiedOnly}
              onChange={(e) => { setVerifiedOnly(e.target.checked); setReviewPage(1); }}
              className="rounded border-line-strong text-sage-600 focus:ring-sage-500"
            />
            {t('reviews.verifiedOnly')}
          </label>

          {/* Search */}
          <div className="flex items-center gap-1 flex-1 min-w-[200px]">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-muted" aria-hidden="true" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setSearchQuery(searchInput); setReviewPage(1); } }}
                placeholder={t('reviews.searchPlaceholder')}
                className="w-full rounded-md border border-line-strong pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
              />
            </div>
          </div>
        </div>

        {/* Photo Gallery */}
        {galleryImages.length > 0 && <ReviewGallery images={galleryImages} className="mb-6" />}

        {reviewsLoading ? (
          <div className="flex items-center justify-center py-8" aria-busy="true">
            <Loader2 className="h-6 w-6 animate-spin text-sage-600" aria-hidden="true" />
            <span className="sr-only">{t('reviews.loadingAriaLabel')}</span>
          </div>
        ) : reviews.length === 0 ? (
          <div className="py-8 text-center">
            <Star className="mx-auto h-10 w-10 text-ink-muted" aria-hidden="true" />
            <p className="mt-2 text-ink-soft">{t('reviews.noReviewsBeFirst')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {reviews.map((review: any) => (
              <article key={review.id} className="border-b border-line pb-6 last:border-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <StarRating rating={review.rating} />
                    <h4 className="mt-1 font-semibold text-ink">
                      {review.title || t('reviews.noTitle')}
                    </h4>
                  </div>
                  <time className="text-xs text-ink-muted shrink-0" dateTime={review.created_at}>
                    {formatDate(review.created_at)}
                  </time>
                </div>

                <p className="mt-2 text-sm text-ink-soft">{review.body}</p>

                {/* Pros & Cons */}
                {(review.pros?.length > 0 || review.cons?.length > 0) && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {review.pros?.length > 0 && (
                      <div>
                        {review.pros.map((pro: string, i: number) => (
                          <div key={i} className="flex items-center gap-1.5 text-sm text-sage-700">
                            <CheckCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            {pro}
                          </div>
                        ))}
                      </div>
                    )}
                    {review.cons?.length > 0 && (
                      <div>
                        {review.cons.map((con: string, i: number) => (
                          <div key={i} className="flex items-center gap-1.5 text-sm text-red-600">
                            <X className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            {con}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Attribute Ratings */}
                {review.attribute_ratings && Object.keys(review.attribute_ratings).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-3">
                    {Object.entries(review.attribute_ratings).map(([key, val]) => {
                      const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
                      return (
                        <span key={key} className="text-xs text-ink-muted">
                          {t('reviews.attributeRating', { label, value: val as number })}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Review images */}
                {review.images?.length > 0 && (
                  <div className="flex gap-2 mt-3">
                    {review.images.map((img: any) => (
                      <img
                        key={img.id}
                        src={img.url}
                        alt={img.alt_text || t('reviews.imageAlt')}
                        className="w-16 h-16 rounded-md object-cover border border-line"
                        loading="lazy"
                      />
                    ))}
                  </div>
                )}

                {/* Admin response */}
                {review.admin_response && (
                  <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-800 mb-1">{t('reviews.responseFrom', { name: SITE_SHORT_NAME })}</p>
                    <p className="text-sm text-blue-700">{review.admin_response}</p>
                  </div>
                )}

                {/* Footer: author + actions */}
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-ink-muted">
                      {t('reviews.byAuthor', {
                        name: review.first_name
                          ? `${review.first_name} ${review.last_initial || ''}`.trim()
                          : t('reviews.anonymous'),
                      })}
                    </p>
                    {review.is_verified_purchase && (
                      <span className="inline-flex items-center gap-0.5 text-xs text-sage-700">
                        <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                        {t('reviews.verifiedPurchase')}
                      </span>
                    )}
                    {review.reviewer_badge && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-xs text-amber-700">
                        {review.reviewer_badge.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Helpful */}
                    <button
                      onClick={() => handleHelpful(review.id)}
                      className="flex items-center gap-1 text-xs text-ink-muted hover:text-sage-600 transition-colors"
                      aria-label={t('reviews.helpfulAriaLabel', { count: review.helpful_count })}
                    >
                      <ThumbsUp className="h-3.5 w-3.5" />
                      {t('reviews.helpfulWithCount', { count: review.helpful_count })}
                    </button>

                    {/* Share */}
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}/product/${slug}?review=${review.id}`;
                        navigator.clipboard?.writeText(url);
                        addToast(t('reviews.shareLinkCopied'), 'success');
                      }}
                      className="flex items-center gap-1 text-xs text-ink-muted hover:text-sage-600 transition-colors"
                      aria-label={t('reviews.shareAriaLabel')}
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      {t('reviews.share')}
                    </button>

                    {/* Report */}
                    <button
                      onClick={() => setReportingReview(review.id)}
                      className="flex items-center gap-1 text-xs text-ink-muted hover:text-red-500 transition-colors"
                      aria-label={t('reviews.reportAriaLabel')}
                    >
                      <Flag className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </article>
            ))}

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <button
                  onClick={() => setReviewPage(Math.max(1, reviewPage - 1))}
                  disabled={reviewPage <= 1}
                  className="px-3 py-1.5 rounded border border-line-strong text-sm disabled:opacity-50"
                >
                  {t('reviews.pagination.previous')}
                </button>
                <span className="px-3 py-1.5 text-sm text-ink-muted">
                  {t('reviews.pagination.pageOf', { current: reviewPage, total: pagination.total_pages })}
                </span>
                <button
                  onClick={() => setReviewPage(Math.min(pagination.total_pages, reviewPage + 1))}
                  disabled={reviewPage >= pagination.total_pages}
                  className="px-3 py-1.5 rounded border border-line-strong text-sm disabled:opacity-50"
                >
                  {t('reviews.pagination.next')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Write a Review form */}
        <div className="mt-8">
          <ReviewForm
            productId={product.id}
            productSlug={slug || ''}
          />
        </div>

        {/* Report Modal */}
        {reportingReview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setReportingReview(null)}>
            <div className="bg-surface rounded-xl p-6 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-ink mb-4">{t('reviews.report.modal.title')}</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-1">{t('reviews.report.modal.reasonLabel')}</label>
                  <select
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    className="w-full rounded-md border border-line-strong px-3 py-2 text-sm"
                  >
                    <option value="spam">{t('reviews.report.modal.reasons.spam')}</option>
                    <option value="inappropriate">{t('reviews.report.modal.reasons.inappropriate')}</option>
                    <option value="fake">{t('reviews.report.modal.reasons.fake')}</option>
                    <option value="other">{t('reviews.report.modal.reasons.other')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-1">{t('reviews.report.modal.detailsLabel')}</label>
                  <textarea
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-line-strong px-3 py-2 text-sm"
                    placeholder={t('reviews.report.modal.detailsPlaceholder')}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setReportingReview(null)}
                  className="px-4 py-2 text-sm text-ink-soft hover:text-ink"
                >
                  {t('reviews.report.modal.cancel')}
                </button>
                <button
                  onClick={handleReport}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  {t('reviews.report.modal.submit')}
                </button>
              </div>
            </div>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}

function RelatedProducts({ product }: { product: any }) {
  const { t } = useTranslation('product');
  const { data, isLoading } = useProducts({
    category: product.category_slug || product.category?.slug,
    limit: 4,
  });
  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);
  const openCartDrawer = useUIStore((s) => s.openCartDrawer);
  const { data: settingsData } = useSettings();
  const upsellConfig = settingsData?.settings?.upsell_config;

  const related = (data?.products ?? []).filter(
    (p: any) => p.id !== product.id
  ).slice(0, 4);

  if (isLoading || related.length === 0) return null;

  async function handleQuickAdd(variantId: string, name: string) {
    try {
      await addItem(variantId, 1);
      if (upsellConfig?.cart_drawer?.enabled) {
        openCartDrawer();
      } else {
        addToast(t('toasts.addedToCart', { name }), 'success');
      }
    } catch {
      addToast(t('toasts.addToCartFailed'), 'error');
    }
  }

  return (
    <section aria-labelledby="related-heading" className="mt-16">
      <h2
        id="related-heading"
        className="text-2xl font-bold text-ink"
      >
        {t('related.heading')}
      </h2>
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {related.map((p: any) => {
          const img = p.images?.[0]?.url ?? p.image_url;
          const price = p.variants?.[0]?.price ?? p.price ?? 0;
          const variantId = p.variants?.[0]?.id;

          return (
            <article
              key={p.id}
              className="group relative rounded-xl border border-line bg-surface shadow-sm overflow-hidden transition-shadow hover:shadow-md"
            >
              <Link
                to={`/product/${p.slug}`}
                className="block aspect-square overflow-hidden bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-inset"
              >
                {img ? (
                  <img
                    src={img}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <BrandSymbol className="h-12 w-12 text-ink-muted" />
                  </div>
                )}
              </Link>
              <div className="p-4">
                <Link
                  to={`/product/${p.slug}`}
                  className="block text-sm font-semibold text-ink hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
                >
                  <h3>{p.name}</h3>
                </Link>
                {p.short_description && (
                  <p className="mt-1 text-xs text-ink-muted line-clamp-2">{p.short_description}</p>
                )}
                {(p.average_rating || p.avg_rating) && (
                  <div className="mt-1.5 flex items-center gap-1">
                    <StarRating rating={p.average_rating || p.avg_rating} />
                    {(p.review_count != null) && (
                      <span className="text-xs text-ink-muted">({p.review_count})</span>
                    )}
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between">
                  <p className="font-bold text-sage-700">
                    {t('related.fromPrice', { price: formatCurrency(price) })}
                  </p>
                  {variantId && (
                    <button
                      onClick={() => handleQuickAdd(variantId, p.name)}
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full bg-sage-600 text-white transition-opacity',
                        'hover:bg-sage-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2',
                        'lg:hover-reveal'
                      )}
                      aria-label={t('related.addToCartAriaLabel', { name: p.name })}
                    >
                      <ShoppingCart className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default function ProductDetail() {
  const { t } = useTranslation('product');
  const { slug } = useParams<{ slug: string }>();
  const { data: product, isLoading, isError } = useProduct(slug || '');
  const { data: settingsData } = useSettings();
  const upsellConfig = settingsData?.settings?.upsell_config;

  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);
  const openCartDrawer = useUIStore((s) => s.openCartDrawer);
  const { addItem: addRecentlyViewed } = useRecentlyViewed();
  const user = useAuthStore((s) => s.user);

  const variants = product?.variants ?? [];
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const addToCartRef = useRef<HTMLDivElement>(null);

  // Track recently viewed
  useEffect(() => {
    if (product) {
      addRecentlyViewed({
        id: product.id,
        name: product.name,
        slug: product.slug,
        price: product.variants?.[0]?.price ?? product.base_price ?? 0,
        image_url: product.images?.[0]?.url ?? product.image_url ?? '',
      });
      trackViewedProduct({
        id: product.id,
        name: product.name,
        slug: product.slug,
        sku: product.sku ?? null,
        price: product.variants?.[0]?.price ?? product.base_price ?? 0,
        compare_at_price: product.variants?.[0]?.compare_at_price ?? product.compare_at_price ?? null,
        image_url: product.images?.[0]?.url ?? product.image_url ?? null,
        categories: (product.categories ?? []).map((c: any) => c.name).filter(Boolean),
        url: `${window.location.origin}/product/${product.slug}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Set default variant when product loads
  const defaultVariantId = variants[0]?.id;
  const activeVariantId = selectedVariantId || defaultVariantId || '';
  const activeVariant = variants.find((v: any) => v.id === activeVariantId) || variants[0];

  const price = activeVariant?.price ?? product?.price ?? 0;
  const compareAtPrice = activeVariant?.compare_at_price ?? product?.compare_at_price;
  const maxQuantity = activeVariant?.available_quantity ?? 99;

  const images = product?.images ?? [];
  if (images.length === 0 && product?.image_url) {
    images.push({ id: 'main', url: product.image_url, alt: product.name });
  }

  // Fetch reviews for JSON-LD structured data
  const { data: jsonLdReviewsData } = useProductReviews(slug || '', 1);
  const jsonLdReviews = jsonLdReviewsData?.reviews ?? [];

  const handleAddToCart = useCallback(async () => {
    if (!activeVariantId) return;
    setIsAdding(true);
    try {
      await addItem(activeVariantId, quantity);
      if (upsellConfig?.cart_drawer?.enabled) {
        openCartDrawer();
      } else {
        addToast(t('toasts.addedToCart', { name: product.name }), 'success');
      }
      setQuantity(1);
    } catch {
      addToast(t('toasts.addToCartFailed'), 'error');
    } finally {
      setIsAdding(false);
    }
  }, [activeVariantId, quantity, addItem, upsellConfig, openCartDrawer, addToast, product?.name, t]);

  const navigate = useNavigate();
  const handleGetForFree = useCallback(async () => {
    if (!activeVariantId) return;
    setIsAdding(true);
    try {
      // Add the variant to cart so the /free endpoint picks it up
      await addItem(activeVariantId, 1);
      const api = (await import('@/lib/api')).default;
      await api.post('/checkout/free', {});
      addToast(t('digital.downloadsReady'), 'success');
      navigate('/account/downloads');
    } catch (err: any) {
      addToast(err?.message || t('digital.claimFailed'), 'error');
    } finally {
      setIsAdding(false);
    }
  }, [activeVariantId, addItem, addToast, navigate, t]);

  if (isLoading) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="animate-pulse" aria-busy="true" aria-label={t('loadingAriaLabel')}>
          <div className="h-4 w-48 rounded bg-surface-muted mb-8" />
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="aspect-square rounded-xl bg-surface-muted" />
            <div className="space-y-4">
              <div className="h-8 rounded bg-surface-muted w-3/4" />
              <div className="h-6 rounded bg-surface-muted w-1/4" />
              <div className="h-4 rounded bg-surface-muted w-full" />
              <div className="h-4 rounded bg-surface-muted w-2/3" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (isError || !product) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 text-center">
        <BrandSymbol className="mx-auto h-16 w-16 text-ink-muted" />
        <h1 className="mt-4 text-2xl font-bold text-ink">{t('notFound.heading')}</h1>
        <p className="mt-2 text-ink-soft">
          {t('notFound.description')}
        </p>
        <Link
          to="/shop"
          className={cn(
            'mt-6 inline-flex items-center justify-center font-medium rounded-lg transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2',
            'h-10 px-4 text-sm gap-2',
            'border-2 border-sage-600 text-sage-600 hover:bg-sage-50 active:bg-sage-100'
          )}
        >
          {t('notFound.back')}
        </Link>
      </main>
    );
  }

  return (
    // i18n-exempt: admin-only QuickEdit label, never customer-visible
    <QuickEditWrapper href={`/admin/products/${product.id}`} label={`Edit product: ${product.name}`}>
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <DocumentHead
        title={product.name}
        description={product.short_description || product.meta_description || `Shop ${product.name} from ${SITE_NAME}`}
        ogImage={images[0]?.url}
        ogType="product"
        canonicalUrl={`/product/${product.slug}`}
      />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.short_description || product.meta_description,
        image: images.map((img: any) => img.url).filter(Boolean),
        brand: { '@type': 'Brand', name: SITE_NAME },
        offers: {
          '@type': 'Offer',
          price: price,
          priceCurrency: 'USD',
          availability: (activeVariant?.available_quantity ?? 1) > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        },
        ...(product.avg_rating ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.avg_rating,
            reviewCount: product.review_count || 1,
          },
        } : {}),
        ...(jsonLdReviews.length > 0 ? {
          review: jsonLdReviews.slice(0, 5).map((r: any) => ({
            '@type': 'Review',
            reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
            author: { '@type': 'Person', name: r.first_name ? `${r.first_name} ${r.last_initial || ''}`.trim() : t('reviews.anonymous') },
            reviewBody: r.body || r.title || '',
            datePublished: r.created_at,
          })),
        } : {}),
      }} />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumbs.home'), item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: t('breadcrumbs.shop'), item: `${SITE_URL}/shop` },
          ...(product.category_name ? [{
            '@type': 'ListItem', position: 3,
            name: product.category_name,
            item: `${SITE_URL}/shop/${product.category_slug || ''}`,
          }] : []),
          {
            '@type': 'ListItem',
            position: product.category_name ? 4 : 3,
            name: product.name,
          },
        ],
      }} />
      {/* Breadcrumbs */}
      <nav aria-label={t('breadcrumbs.label')} className="mb-8">
        <ol className="flex items-center gap-1.5 text-sm text-ink-muted">
          <li>
            <Link
              to="/"
              className="hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
            >
              {t('breadcrumbs.home')}
            </Link>
          </li>
          <li aria-hidden="true">
            <ChevronRight className="h-4 w-4" />
          </li>
          <li>
            <Link
              to="/shop"
              className="hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
            >
              {t('breadcrumbs.shop')}
            </Link>
          </li>
          {product.category_name && (
            <>
              <li aria-hidden="true">
                <ChevronRight className="h-4 w-4" />
              </li>
              <li>
                <Link
                  to={`/shop/${product.category_slug || ''}`}
                  className="hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
                >
                  {product.category_name}
                </Link>
              </li>
            </>
          )}
          <li aria-hidden="true">
            <ChevronRight className="h-4 w-4" />
          </li>
          <li>
            <span className="font-medium text-ink" aria-current="page">
              {product.name}
            </span>
          </li>
        </ol>
      </nav>

      {/* Product section */}
      <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
        {/* Image gallery */}
        <ImageGallery images={images} />

        {/* Product info */}
        <div className="space-y-6">
          {product.category_name && (
            <Badge variant="green">{product.category_name}</Badge>
          )}

          <h1 className="text-3xl font-bold text-ink">{product.name}</h1>

          {/* Countdown Timer */}
          <CountdownTimer productId={product.id} />

          {/* Price */}
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-bold text-sage-700">
              {formatCurrency(price)}
            </span>
            {compareAtPrice && compareAtPrice > price && (
              <>
                <span className="text-lg text-ink-muted line-through">
                  {formatCurrency(compareAtPrice)}
                </span>
                <Badge variant="red">
                  {t('save', { percent: Math.round(((compareAtPrice - price) / compareAtPrice) * 100) })}
                </Badge>
              </>
            )}
          </div>

          {/* Volume Discount Table */}
          <VolumeDiscountTable rules={product.volume_discounts} basePrice={price} />

          {/* Short description */}
          {product.short_description && (
            <p className="text-ink-soft">{product.short_description}</p>
          )}

          {/* Variant selector */}
          <VariantSelector
            variants={variants}
            selectedId={activeVariantId}
            onChange={setSelectedVariantId}
          />

          {/* Quantity + Add to Cart + Wishlist */}
          <div ref={addToCartRef} className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div>
              <span className="mb-1 block text-sm font-medium text-ink-soft">
                {t('quantity')}
              </span>
              <QuantityInput
                value={quantity}
                onChange={setQuantity}
                max={maxQuantity}
              />
            </div>
            {(() => {
              const isDigital = product?.is_digital === true;
              const variantPrice = Number(activeVariant?.price ?? product?.base_price ?? 0);
              const isFreeDigital = isDigital && variantPrice === 0;
              const filesSummary = (activeVariant as any)?.files_summary as
                | { count: number; total_size_bytes: number; formats: string[] }
                | undefined;

              if (isFreeDigital) {
                if (!user) {
                  const returnTo = encodeURIComponent(window.location.pathname);
                  return (
                    <Button
                      size="lg"
                      className="flex-1 sm:flex-initial"
                      onClick={() => navigate(`/login?return=${returnTo}`)}
                      disabled={!activeVariantId}
                    >
                      {t('digital.signInToDownload')}
                    </Button>
                  );
                }
                return (
                  <Button
                    size="lg"
                    className="flex-1 sm:flex-initial"
                    onClick={handleGetForFree}
                    isLoading={isAdding}
                    disabled={!activeVariantId}
                  >
                    {t('digital.getForFree')}
                  </Button>
                );
              }

              return (
                <>
                  <Button
                    size="lg"
                    className="flex-1 sm:flex-initial"
                    onClick={handleAddToCart}
                    isLoading={isAdding}
                    disabled={!activeVariantId || (!isDigital && activeVariant?.available_quantity === 0)}
                  >
                    <ShoppingCart className="h-5 w-5" aria-hidden="true" />
                    {!activeVariantId
                      ? t('unavailable')
                      : !isDigital && activeVariant?.available_quantity === 0
                        ? t('outOfStock')
                        : t('addToCart')}
                  </Button>
                  {isDigital && filesSummary && filesSummary.count > 0 && (
                    <p className="text-xs text-ink-muted self-center ml-3">
                      {filesSummary.formats.length > 0
                        ? t('digital.filesSummary', {
                            count: filesSummary.count,
                            formats: filesSummary.formats.map((f) => f.toUpperCase()).join(', '),
                          })
                        : t('digital.filesSummaryNoFormats', { count: filesSummary.count })}
                    </p>
                  )}
                </>
              );
            })()}
            <WishlistButton productId={product.id} />
          </div>

          {/* Stock indicator */}
          <LowStockIndicator
            availableQuantity={activeVariant?.available_quantity ?? 99}
            stockStatus={activeVariant?.stock_status}
          />
          {activeVariant?.available_quantity === 0 && (
            <BackInStockButton variantId={activeVariantId} />
          )}

          {/* Growing info badges */}
          <GrowingInfoBadges product={product} />

          {/* Product tags */}
          <ProductTags tags={product.tags} />

          {/* Social sharing */}
          <SocialShareButtons product={product} imageUrl={images[0]?.url} />
        </div>
      </div>

      {/* Frequently Bought Together */}
      <FrequentlyBoughtTogether productId={product.id} currentPrice={price} />

      {/* Product Bundles */}
      <ProductBundles productId={product.id} />

      {/* Tabs */}
      <div className="mt-12">
        <Tabs product={product} />
      </div>

      {/* Recently Viewed */}
      <RecentlyViewed excludeId={product.id} />

      {/* Related products */}
      <RelatedProducts product={product} />

      {/* Sticky mobile add-to-cart */}
      <StickyAddToCart
        productName={product.name}
        price={activeVariant?.price ?? product.base_price ?? 0}
        variantId={activeVariant?.id}
        targetRef={addToCartRef}
      />
    </main>
    </QuickEditWrapper>
  );
}
